import { type FormEvent, useState } from "react";
import { Link, useNavigate } from "react-router";
import type { DraftDTO, ScheduledMessageDTO } from "@justone/shared";
import { useDeleteDraft, useDrafts, useSaveDraft, useScheduled, useScheduleMutations } from "../api/compose";
import { errorMessage } from "../api/http";
import { useChannels, useConversations, useMe, useUserMap, useUsers } from "../api/queries";
import { ScheduleDialog } from "../components/ScheduleDialog";
import { Dialog, EmptyState, Field, Icon, Skeleton, useConfirm, useToast } from "../components/ui";
import { formatDay, formatTime, formatWhen } from "../lib/format";
import { fromBody } from "../lib/mentionTags";
import { dmTitle } from "../lib/room";
import { PageShell } from "./OtherPages";

type Place = Pick<DraftDTO, "channelId" | "conversationId" | "parentId">;

/** 어디에 쓰던 글인지와 그곳 주소 */
function usePlace() {
  const { data: channels = [] } = useChannels();
  const { data: conversations = [] } = useConversations();
  const users = useUserMap();
  const me = useMe();
  return (p: Place) => {
    const base = p.channelId !== null ? `/c/${p.channelId}` : `/d/${p.conversationId}`;
    const name =
      p.channelId !== null
        ? `#${channels.find((c) => c.id === p.channelId)?.name ?? "채널"}`
        : (() => {
            const conv = conversations.find((c) => c.id === p.conversationId);
            return conv ? `DM · ${dmTitle(conv, users, me.id)}` : "DM";
          })();
    return { label: p.parentId ? `${name} 스레드` : name, path: p.parentId ? `${base}/t/${p.parentId}` : base };
  };
}

/** 미리보기: "@kernel"은 "@황승현 (kernel)"로, 마크다운 기호는 빼고 한 줄로 */
function usePreview() {
  const { data: users = [] } = useUsers();
  return (body: string) =>
    fromBody(body, users)
      .text.replace(/[*_~`>#[\]]/g, "")
      .replace(/\s+/g, " ")
      .trim()
      .slice(0, 160);
}

/**
 * 초안 (슬랙의 "초안 및 보낸 항목"): 보내지 않고 둔 글과 예약한 메시지.
 * 초안을 누르면 그곳으로 가서 이어 쓴다.
 */
export function DraftsPage() {
  const drafts = useDrafts();
  const scheduled = useScheduled();
  const pending = drafts.isPending || scheduled.isPending;
  const none = !pending && !drafts.data?.length && !scheduled.data?.length;
  return (
    <PageShell title="초안">
      {pending ? (
        <Skeleton kind="list" rows={4} />
      ) : none ? (
        <EmptyState icon="inbox" title="초안이 없습니다">
          보내지 않고 둔 글은 몇 초 뒤 여기에 저장되어 다른 기기에서도 이어 쓸 수 있습니다. 보내기 단추 옆 화살표로 메시지를 예약할 수도 있습니다.
        </EmptyState>
      ) : (
        <>
          {!!scheduled.data?.length && (
            <section aria-label="예약됨">
              <h2 className="list-heading">예약됨</h2>
              <div className="list">
                {scheduled.data.map((s) => (
                  <ScheduledRow key={s.id} s={s} />
                ))}
              </div>
            </section>
          )}
          {!!drafts.data?.length && (
            <section aria-label="초안">
              <h2 className="list-heading">보내지 않은 글</h2>
              <div className="list">
                {drafts.data.map((d) => (
                  <DraftRow key={d.id} d={d} />
                ))}
              </div>
            </section>
          )}
        </>
      )}
    </PageShell>
  );
}

function DraftRow({ d }: { d: DraftDTO }) {
  const place = usePlace()(d);
  const preview = usePreview();
  const remove = useDeleteDraft();
  const save = useSaveDraft();
  const toast = useToast();
  const drop = () =>
    remove.mutate(d.id, {
      onSuccess: () =>
        toast("초안을 지웠습니다.", {
          label: "되돌리기",
          run: () => save.mutate({ channelId: d.channelId ?? undefined, conversationId: d.conversationId ?? undefined, parentId: d.parentId ?? undefined, body: d.body }),
        }),
      onError: (e) => toast(errorMessage(e)),
    });
  return (
    <div className="list-row draft-row">
      <Link to={place.path} className="grow draft-main">
        <span className="sub">
          {place.label} · {formatDay(d.updatedAt)} {formatTime(d.updatedAt)}
        </span>
        <span className="title">{preview(d.body)}</span>
      </Link>
      <button type="button" className="icon-btn" onClick={drop} aria-label={`${place.label} 초안 지우기`}>
        <Icon name="close" />
      </button>
    </div>
  );
}

function ScheduledRow({ s }: { s: ScheduledMessageDTO }) {
  const place = usePlace()(s);
  const preview = usePreview();
  const ops = useScheduleMutations();
  const toast = useToast();
  const confirm = useConfirm();
  const navigate = useNavigate();
  const [rescheduling, setRescheduling] = useState(false);
  const [editing, setEditing] = useState(false);
  const failed = s.status === "failed";
  const text = preview(s.body) || (s.attachments.length ? `파일 ${s.attachments.length}개` : "");

  const sendNow = () =>
    ops.sendNow.mutate(s.id, {
      onSuccess: (r) => toast("보냈습니다.", { label: "보기", run: () => navigate(r.path) }),
      onError: (e) => toast(errorMessage(e)),
    });
  const cancel = async () => {
    if (!(await confirm({ title: "예약 취소", body: "이 메시지를 보내지 않고 지웁니다. 붙인 파일도 함께 지워집니다.", confirmLabel: "예약 취소", danger: true }))) return;
    ops.cancel.mutate(s.id, { onSuccess: () => toast("예약을 취소했습니다."), onError: (e) => toast(errorMessage(e)) });
  };

  return (
    <div className={`list-row scheduled-row${failed ? " failed" : ""}`}>
      <div className="grow">
        <span className="sub">
          {place.label} · {failed ? "보내지 못함" : `${formatWhen(s.sendAt)}에 보냄`}
          {s.attachments.length > 0 && text !== `파일 ${s.attachments.length}개` ? ` · 파일 ${s.attachments.length}개` : ""}
        </span>
        <span className="title">{text}</span>
        {failed && s.error && <span className="form-error">{s.error}</span>}
      </div>
      <div className="row-actions">
        <button type="button" className="btn btn-sm" onClick={sendNow} disabled={ops.sendNow.isPending}>
          지금 보내기
        </button>
        <button type="button" className="btn btn-sm" onClick={() => setRescheduling(true)}>
          {failed ? "다시 예약" : "시간 바꾸기"}
        </button>
        <button type="button" className="btn btn-sm btn-quiet" onClick={() => setEditing(true)}>
          글 고치기
        </button>
        <button type="button" className="btn btn-sm btn-quiet btn-danger" onClick={() => void cancel()}>
          취소
        </button>
      </div>
      {rescheduling && (
        <ScheduleDialog
          title="예약 시간 바꾸기"
          initial={failed ? undefined : new Date(s.sendAt)}
          confirmLabel="바꾸기"
          onClose={() => setRescheduling(false)}
          onPick={(at) => {
            setRescheduling(false);
            ops.update.mutate({ id: s.id, sendAt: at.toISOString() }, { onSuccess: (n) => toast(`${formatWhen(n.sendAt)}에 보냅니다.`), onError: (e) => toast(errorMessage(e)) });
          }}
        />
      )}
      {editing && <EditScheduledDialog s={s} onClose={() => setEditing(false)} />}
    </div>
  );
}

function EditScheduledDialog({ s, onClose }: { s: ScheduledMessageDTO; onClose: () => void }) {
  const [body, setBody] = useState(s.body);
  const ops = useScheduleMutations();
  const toast = useToast();
  const submit = (e: FormEvent) => {
    e.preventDefault();
    ops.update.mutate({ id: s.id, body }, { onSuccess: onClose, onError: (err) => toast(errorMessage(err)) });
  };
  return (
    <Dialog open onClose={onClose} label="예약 메시지 고치기">
      <form onSubmit={submit}>
        <h2>예약 메시지 고치기</h2>
        <Field id="scheduled-body" label="메시지">
          <textarea id="scheduled-body" className="input" rows={5} required maxLength={8000} value={body} onChange={(e) => setBody(e.target.value)} autoFocus />
        </Field>
        <div className="buttons">
          <button type="button" className="btn" onClick={onClose}>
            취소
          </button>
          <button type="submit" className="btn btn-primary" disabled={ops.update.isPending || !body.trim()}>
            저장
          </button>
        </div>
      </form>
    </Dialog>
  );
}
